'use client';

import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { FiRefreshCw } from "react-icons/fi";
import NavBar from '@/components/header/NavBar';
import { useLocale } from 'next-intl';
import Dubble from '@/components/home-page/dubbleSection';
import { GlowingEffect } from '@/components/home-page/glowing-effect';
import { Box, Lock, Search, Settings, Sparkles } from "lucide-react";

import Why from '@/components/home-page/whySection';
import ContactForm from '@/components/home-page/ContactForm';
import FAQContainer from '@/components/home-page/FAQContainer';
import Footer from '@/components/footer/Footer';


const ContactForms: React.FC = () => {

  return (<>
  {/* Fixed background — stays behind everything while page scrolls */}
  <div
    className="bg-cover bg-center bg-no-repeat h-96 lg:h-[500px] relative"
    style={{ backgroundImage: `url('/hero/commerce.avif')` }}
  >
   
<div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[500px] " />
  <h1 className="absolute inset-0 flex items-center pt-20 lg:pt-40 justify-center text-white text-5xl lg:text-9xl font-extrabold text-center px-4 ">
   Commerce & Hospitality
  </h1>
    
  </div>
 

  <NavBar />
   <ul className="grid grid-cols-1 bg-secondary grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 p-8 xl:grid-rows-2">
      <GridItem
        area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
        icon={<Box className="h-4 w-4 text-black dark:text-primary" />}
        title="E-Commerce"
        description="We build fast, secure online stores that make it easy for customers to browse, buy, and come back — from product pages to checkout and payments."
      />
 
      <GridItem
        area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
        icon={<Settings className="h-4 w-4 text-black dark:text-primary" />}
        title="Restaurants & Cafés"
        description="We create websites and ordering systems that showcase your menu, simplify reservations, and make it effortless for guests to order online or book a table."
      />
 
      <GridItem
        area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
        icon={<Lock className="h-4 w-4 text-black dark:text-primary" />}
        title="Hotels & Accommodation"
        description="We build secure, reliable booking platforms for hotels and short-term rentals, helping streamline reservations and deliver seamless experiences for guests."
      />
 
      <GridItem
        area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
        icon={<Sparkles className="h-4 w-4 text-black dark:text-primary" />}
        title="Retail & Local Business"
        description="We create high-performing digital solutions that help retail and local businesses attract customers, showcase products, and turn online visibility into in-store and online sales."
      />
 
      <GridItem
        area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
        icon={<Search className="h-4 w-4 text-black dark:text-primary" />}
        title="Events & Bookings"
        description="We build smart booking and scheduling systems that simplify reservations, manage availability, and create seamless experiences for businesses and their customers."
      />
    </ul>
<Why/>
<ContactForm/>

<FAQContainer
  title={"Commerce &\nHospitality FAQs"}
  description="Answers to the most common questions clients ask about our solutions for e-commerce, restaurants, hotels, and hospitality businesses."
  faq={[
    {
      question: "Can you build an online store for my business?",
      answer:
        "Yes, we build custom e-commerce sites with secure checkout and payment integration, tailored to your products and brand.",
    },
    {
      question: "Do you build online ordering or reservation systems?",
      answer:
        "Yes, we create ordering and booking systems for restaurants, cafés, and hospitality businesses, making it easy for customers to order or reserve online.",
    },
    {
      question: "Can you build a booking platform for a hotel or rental?",
      answer:
        "Absolutely. We build secure, reliable booking platforms that manage availability and reservations for hotels and short-term rentals.",
    },
    {
      question: "Will my site work well on mobile for customers on the go?",
      answer:
        "Yes, every site is built mobile-first, since most commerce and hospitality customers browse and book from their phones.",
    },
    {
      question: "Can you integrate payment processors and POS systems?",
      answer:
        "Yes, we integrate popular payment processors and can connect with existing POS or inventory systems depending on your setup.",
    },
    {
      question: "Do you offer support after the site or system launches?",
      answer:
        "Yes, we offer ongoing maintenance and support plans to keep your store, booking system, or site running smoothly after launch.",
    },
  ]}
/>
<Footer/>
</>
  );
};

export default ContactForms;


interface GridItemProps {
  area: string;
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}
 
const GridItem = ({ area, icon, title, description }: GridItemProps) => {
  return (
    <li className={`min-h-[14rem] list-none  rounded-3xl ${area}`}>
      <div className="relative h-full rounded-2xl border p-2 md:rounded-3xl md:p-3">
     <GlowingEffect

  glow={true}
  disabled={false}
  spread={40}
  proximity={64}
  inactiveZone={0.01}
  borderWidth={2}
/>
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 ">
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="w-fit rounded-lg border text-primary border-white p-2">
              {icon}
            </div>
            <div className="space-y-3">
              <h3 className="-tracking-4 mt-8 text-xl  font-bold text-balance text-black md:text-3xl dark:text-primary">
                {title}
              </h3>
              <h2 className="text-sm/[1.125rem] text-black md:text-xl dark:text-white  font-light">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};